"use client";

import Link from "next/link";
import { useState } from "react";
import { ActionForm, SubmitButton } from "@/components/ui/form";
import { useT } from "@/components/i18n-ui/provider";
import { loginAction, signupAction, forgotPasswordAction, resetPasswordAction, twoFactorLoginAction, verifyEmailAction } from "./actions";

export function SignupForm({ idea, next }: { idea?: string; next?: string }) {
  const t = useT("auth");
  return (
    <ActionForm action={signupAction} className="space-y-4" refresh={false}>
      <input type="hidden" name="idea" value={idea ?? ""} />
      <input type="hidden" name="next" value={next ?? ""} />
      <div><label className="label" htmlFor="name">{t("fields.name")}</label><input className="input" id="name" name="name" required autoComplete="name" /></div>
      <div><label className="label" htmlFor="email">{t("fields.email")}</label><input className="input" id="email" name="email" type="email" required autoComplete="email" dir="ltr" /></div>
      <div>
        <label className="label" htmlFor="password">{t("fields.password")}</label>
        <input className="input" id="password" name="password" type="password" required minLength={10} autoComplete="new-password" dir="ltr" />
        <p className="hint">{t("fields.passwordHint")}</p>
      </div>
      <SubmitButton className="btn-primary w-full" pendingText={t("signup.pending")}>{t("signup.submit")}</SubmitButton>
      <p className="text-xs text-ink-500">
        {t.rich("signup.legal", {
          terms: (c) => <Link className="link" href="/terms">{c}</Link>,
          privacy: (c) => <Link className="link" href="/privacy">{c}</Link>,
        })}
      </p>
    </ActionForm>
  );
}

export function LoginForm({ next }: { next?: string }) {
  const t = useT("auth");
  return (
    <ActionForm action={loginAction} className="space-y-4" refresh={false}>
      <input type="hidden" name="next" value={next ?? "/dashboard"} />
      <div><label className="label" htmlFor="email">{t("fields.email")}</label><input className="input" id="email" name="email" type="email" required autoComplete="email" dir="ltr" /></div>
      <div>
        <div className="flex items-center justify-between"><label className="label" htmlFor="password">{t("fields.password")}</label><Link href="/forgot-password" className="mb-1.5 text-xs link">{t("login.forgot")}</Link></div>
        <input className="input" id="password" name="password" type="password" required autoComplete="current-password" dir="ltr" />
      </div>
      <SubmitButton className="btn-primary w-full" pendingText={t("login.pending")}>{t("login.submit")}</SubmitButton>
    </ActionForm>
  );
}

export function ForgotForm() {
  const t = useT("auth");
  return (
    <ActionForm action={forgotPasswordAction} className="space-y-4" refresh={false}>
      <div><label className="label" htmlFor="email">{t("fields.email")}</label><input className="input" id="email" name="email" type="email" required autoComplete="email" dir="ltr" /></div>
      <SubmitButton className="btn-primary w-full">{t("forgot.submit")}</SubmitButton>
    </ActionForm>
  );
}

export function ResetForm({ token }: { token: string }) {
  const t = useT("auth");
  return (
    <ActionForm action={resetPasswordAction} className="space-y-4" refresh={false}>
      <input type="hidden" name="token" value={token} />
      <div><label className="label" htmlFor="password">{t("fields.newPassword")}</label><input className="input" id="password" name="password" type="password" required minLength={10} autoComplete="new-password" dir="ltr" /></div>
      <SubmitButton className="btn-primary w-full">{t("reset.submit")}</SubmitButton>
    </ActionForm>
  );
}

export function TwoFactorForm({ next }: { next?: string }) {
  const t = useT("auth");
  const [recovery, setRecovery] = useState(false);
  return (
    <ActionForm action={twoFactorLoginAction} className="space-y-4" refresh={false}>
      <input type="hidden" name="next" value={next ?? "/dashboard"} />
      {recovery ? (
        <div key="recovery">
          <label className="label" htmlFor="code">{t("twoFactor.recoveryLabel")}</label>
          <input className="input font-mono tracking-wider" id="code" name="code" required autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="xxxx-xxxx-xxxx" maxLength={20} autoFocus dir="ltr" />
          <p className="hint">{t("twoFactor.recoveryHint")}</p>
        </div>
      ) : (
        <div key="totp">
          <label className="label" htmlFor="code">{t("twoFactor.codeLabel")}</label>
          <input className="input text-center font-mono text-lg tracking-[0.4em]" id="code" name="code" required inputMode="numeric" pattern="[0-9 ]{6,7}" maxLength={7} autoComplete="one-time-code" placeholder="000000" autoFocus dir="ltr" />
          <p className="hint">{t("twoFactor.codeHint")}</p>
        </div>
      )}
      <SubmitButton className="btn-primary w-full" pendingText={t("twoFactor.pending")}>{t("twoFactor.submit")}</SubmitButton>
      <button type="button" className="link text-sm" onClick={() => setRecovery((r) => !r)}>
        {recovery ? t("twoFactor.useApp") : t("twoFactor.useRecovery")}
      </button>
    </ActionForm>
  );
}

export function VerifyEmailForm({ token }: { token: string }) {
  const t = useT("auth");
  return (
    <ActionForm action={verifyEmailAction} className="space-y-4" refresh={false}>
      <input type="hidden" name="token" value={token} />
      <SubmitButton className="btn-primary w-full" pendingText={t("verify.pending")}>{t("verify.submit")}</SubmitButton>
    </ActionForm>
  );
}
